import { Box, Typography, createFilterOptions } from "@mui/material"
import type { FC } from "react"
import { useCallback, useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { type VendorOption, useVendorOptions } from "../../hooks/useVendorOptions"
import type { InvoiceFormValues } from "../../schemas/invoiceForm"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import type { Invoice } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface VendorAutocompleteProps {
  disabled?: boolean
  // Loaded invoice in edit/view modes. `useVendorOptions` is paginated and search-driven,
  // so the saved vendor is often missing from its result set. Synthesizing a fallback
  // option from `invoice.vendorCompany` lets the autocomplete display the saved Vendor
  // Name when no matching option is loaded yet.
  invoice?: Invoice
  // Previously-selected vendor option passed from the parent in create mode. Survives the
  // form unmount that happens while the duplicate warning dialog is shown, so the vendor
  // label is still visible when the form remounts after "Go Back". AK-6332.
  selectedVendor?: VendorOption | null
  onVendorChange?: (option: VendorOption | null) => void
}

interface VendorOptionLabelProps {
  vendor: VendorOption
}

const VendorOptionLabel: FC<VendorOptionLabelProps> = ({ vendor }) => (
  <Box className='flex flex-col py-1'>
    <Typography variant='body2' className='font-medium text-gray-800'>
      {vendor.name}
    </Typography>
    <Typography variant='caption' className='text-gray-600'>
      {`Aeros#${vendor.aerosNumber} · AS400#${vendor.as400Number} · ${vendor.accountType} · ${vendor.billingAddress}`}
    </Typography>
  </Box>
)

export const VendorAutocomplete: FC<VendorAutocompleteProps> = ({
  disabled,
  invoice,
  selectedVendor,
  onVendorChange,
}) => {
  const { control, setValue } = useFormContext<InvoiceFormValues>()
  const { vendors, isFetching, setSearchTerm } = useVendorOptions()

  // Anchor option for cases where the selected vendor is not in the current paginated page.
  // In edit/view mode: synthesized from invoice.vendorCompany (only id+name are reliable;
  // other fields are empty and the entry is suppressed from the dropdown). In create mode
  // after "Go Back" from the duplicate warning: the full VendorOption passed down from
  // useInvoiceForm, which survived the form unmount. AK-6332.
  const loadedInvoiceVendor = useMemo<VendorOption | null>(() => {
    if (!invoice && selectedVendor) return selectedVendor
    const vendorCompany = invoice?.vendorCompany
    if (!vendorCompany?.companyId || !vendorCompany.name) return null
    const billingCountry =
      vendorCompany.locationsAssociations?.find((association) => association.billing)?.billing?.countryIsoCode ?? ""
    return {
      id: vendorCompany.companyId,
      name: vendorCompany.name,
      aerosNumber: "",
      as400Number: "",
      accountType: "",
      billingAddress: "",
      billingCountry,
    }
  }, [invoice, selectedVendor])

  // Append the synthetic only when the paginated list does NOT already include the
  // saved vendor. Keeps MUI's "value must be in options" invariant happy without
  // duplicating a vendor row when it has loaded normally.
  const allVendors = useMemo(() => {
    if (!loadedInvoiceVendor || vendors.some((vendor) => vendor.id === loadedInvoiceVendor.id)) return vendors
    return [...vendors, loadedInvoiceVendor]
  }, [vendors, loadedInvoiceVendor])

  const vendorsById = useMemo(() => new Map(allVendors.map((vendor) => [vendor.id, vendor])), [allVendors])

  const options = useMemo<Option<string>[]>(
    () =>
      allVendors.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
        customLabel: <VendorOptionLabel vendor={vendor} />,
      })),
    [allVendors]
  )

  // The synthetic exists only to anchor `value` in `options`; suppress it from the
  // dropdown so users never see a vendor row with blank Aeros#/AS400#/Account Type.
  // Default substring matching is preserved via `createFilterOptions`.
  const defaultFilter = useMemo(() => createFilterOptions<Option<string>>(), [])
  const filterOptions = useCallback(
    (opts: Option<string>[], state: Parameters<typeof defaultFilter>[1]) => {
      const filtered = defaultFilter(opts, state)
      if (!loadedInvoiceVendor || vendors.some((vendor) => vendor.id === loadedInvoiceVendor.id)) return filtered
      return filtered.filter((option) => option.value !== loadedInvoiceVendor.id)
    },
    [defaultFilter, loadedInvoiceVendor, vendors]
  )

  const handleChange = (value: string | null) => {
    const option = value ? (vendorsById.get(value) ?? null) : null
    onVendorChange?.(option)
    if (!option) return

    if (option.defaultExpenseAccountId) {
      setValue(`expenseLines.0.expenseAccountId`, option.defaultExpenseAccountId, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }

  return (
    <FormAutocomplete
      control={control}
      name='vendorId'
      label='Vendor Name'
      placeholder='Search or select vendor...'
      required
      disabled={disabled}
      loading={isFetching}
      options={options}
      filterOptions={filterOptions}
      onChange={handleChange}
      onInputChange={setSearchTerm}
      data-testid='invoice.form.vendor-field'
    />
  )
}
